import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
} from '@angular/core';
import { NgMaterialModule } from '@principle-theorem/ng-material';
import { NgPrincipleSharedModule } from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  DialogPresets,
  NgSharedModule,
} from '@principle-theorem/ng-shared';
import {
  BigQueryUploadJobStatus,
  IBigQueryUploadJob,
  IBigQueryUploadsConfig,
  IBigQueryUploadStats,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  formatMsDuation,
  MILLIS_IN_MINUTE,
  toMoment,
  WithRef,
} from '@principle-theorem/shared';
import { first, last, sum } from 'lodash';
import * as moment from 'moment-timezone';
import { BigQueryUploadsConfigComponent } from '../big-query-upload-settings/big-query-uploads-config.component';
import { BigQueryUploadStore } from '../big-query-upload.store';
import { BigQueryUploadJob } from '@principle-theorem/principle-core';

interface IStatusSummary {
  outstanding: number;
  outstandingDelta: number;
  pending: number;
  inProgress: number;
  completed: number;
  failed: number;
  failureRate: number;
}

interface ITimingSummary {
  totalRunTime: number;
  averageRunTime: number;
  jobsPerMin: number;
  filesPerMin: number;
}

interface ISummary {
  timeDeltaMs: number;
  jobs: IStatusSummary;
  files: IStatusSummary;
  timing: ITimingSummary;
  averageFilesPerJob: number;
  clearQueueEtaMs: number;
  jobsInFailedFolder: number;
  filesInFailedFolder: number;
}

@Component({
  selector: 'pr-big-query-upload-toolbar',
  templateUrl: './big-query-upload-toolbar.component.html',
  styleUrls: ['./big-query-upload-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgMaterialModule,
    NgSharedModule,
    NgPrincipleSharedModule,
  ],
})
export class BigQueryUploadToolbarComponent {
  private _dialog = inject(BasicDialogService);
  store = inject(BigQueryUploadStore);
  formatMsDuation = formatMsDuation;

  summary: Signal<ISummary>;

  recentDuration = input.required<moment.Duration>();

  constructor() {
    this.summary = computed(() =>
      this._calculateSummary(this.store.jobs(), this.store.stats())
    );
  }

  async openSettings(): Promise<void> {
    const config = this.store.config();
    if (!config) {
      return;
    }
    const updates = await this._dialog
      .open<
        BigQueryUploadsConfigComponent,
        IBigQueryUploadsConfig,
        Partial<IBigQueryUploadsConfig>
      >(BigQueryUploadsConfigComponent, DialogPresets.medium({ data: config }))
      .afterClosed()
      .toPromise();
    if (!updates) {
      return;
    }
    await Firestore.patchDoc(BigQueryUploadJob.config(), updates);
  }

  private _calculateSummary(
    jobs: WithRef<IBigQueryUploadJob>[],
    stats: WithRef<IBigQueryUploadStats>[]
  ): ISummary {
    const newestStat = first(stats);
    const oldestStat = last(stats);
    const delta = this.store.calculateStatDelta(oldestStat, newestStat);

    const pending = this.store.filterJobsByStatus(
      jobs,
      BigQueryUploadJobStatus.Pending
    );
    const inProgress = this.store.filterJobsByStatus(
      jobs,
      BigQueryUploadJobStatus.InProgress
    );

    const finished = this.store.filterJobsByStatus(jobs, [
      BigQueryUploadJobStatus.Failed,
      BigQueryUploadJobStatus.Completed,
    ]);
    const completed = this.store.filterJobsByStatus(
      finished,
      BigQueryUploadJobStatus.Completed
    );
    const failed = this.store.filterJobsByStatus(
      finished,
      BigQueryUploadJobStatus.Failed
    );

    const jobsSummary: IStatusSummary = {
      outstanding: newestStat?.stats.total.jobs ?? 0,
      outstandingDelta:
        (newestStat?.stats.total.jobs ?? 0) -
        (oldestStat?.stats.total.jobs ?? 0),
      pending: pending.length,
      inProgress: inProgress.length,
      completed: completed.length,
      failed: failed.length,
      failureRate: failed.length / finished.length,
    };

    const filesSummary: IStatusSummary = {
      outstanding: newestStat?.stats.total.files ?? 0,
      outstandingDelta:
        (newestStat?.stats.total.files ?? 0) -
        (oldestStat?.stats.total.files ?? 0),
      pending: this._fileCount(pending),
      inProgress: this._fileCount(inProgress),
      completed: this._fileCount(completed),
      failed: this._fileCount(failed),
      failureRate: this._fileCount(failed) / this._fileCount(finished),
    };

    const runTimes = finished.map((job) => this._getRunTime(job));
    const timingSummary: ITimingSummary = {
      totalRunTime: sum(runTimes),
      averageRunTime: sum(runTimes) / runTimes.length,
      jobsPerMin: this._perMinute(jobsSummary.completed),
      filesPerMin: this._perMinute(filesSummary.completed),
    };

    const averageFilesPerJob =
      filesSummary.inProgress / jobsSummary.inProgress || 0;

    const clearQueueMins = jobsSummary.outstanding / timingSummary.jobsPerMin;
    const clearQueueEtaMs = clearQueueMins * MILLIS_IN_MINUTE;

    const jobsInFailedFolder = newestStat?.failedStats?.stats.total.jobs ?? 0;
    const filesInFailedFolder = newestStat?.failedStats?.stats.total.files ?? 0;

    return {
      timeDeltaMs: delta.timeDeltaMs,
      jobs: jobsSummary,
      files: filesSummary,
      timing: timingSummary,
      averageFilesPerJob,
      clearQueueEtaMs,
      jobsInFailedFolder,
      filesInFailedFolder,
    };
  }

  private _perMinute(value: number): number {
    return value / this.recentDuration().asMinutes();
  }

  private _getRunTime(job: WithRef<IBigQueryUploadJob>): number {
    const started = toMoment(job.createdAt);
    const hasFinished = [
      BigQueryUploadJobStatus.Completed,
      BigQueryUploadJobStatus.Failed,
    ].includes(job.status);
    const ended = hasFinished ? toMoment(job.updatedAt) : moment();
    toMoment(job.updatedAt);
    return ended.diff(started, 'milliseconds');
  }

  private _fileCount(jobs: WithRef<IBigQueryUploadJob>[]): number {
    return sum(jobs.map((job) => job.files.length));
  }
}
